import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Probability, for one event and then for two.
 *
 * The real subject of the first lesson is not the arithmetic — which is one
 * fraction — but the idea that uncertainty can be measured at all. A student
 * who takes that seriously finds the rest of the strand mostly bookkeeping.
 *
 * The second lesson exists for one wrong answer. Asked for the probability
 * that both of two things happen, students add, because everything they have
 * combined so far has been combined by adding. Multiplying is not a rule to
 * memorise here; it is what "a fraction of a fraction" has always meant.
 */
export const PROBABILITY_LESSONS: Lesson[] = [
  lesson({
    id: 'basic-probability-01',
    unitId: 'unit-probability',
    conceptId: 'basic-probability',
    title: 'Introduction to Probability',
    summary: 'Measuring how likely something is on a scale from 0 to 1 — the first mathematics of what has not happened yet.',
    objectives: [
      'Place an event on the probability scale from 0 to 1',
      'Compute the probability of an event with equally likely outcomes',
      'Find the probability that an event does not happen',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Measuring what has not happened',
        blocks: [
          text('Everything you have calculated so far has been about things that are definitely true. 3 + 4 is 7, and it does not depend on how the afternoon goes.'),
          text('This is different. A coin has not been tossed yet; the weather tomorrow is not settled. **Probability** is the mathematics of assigning a number to how likely something is — and the surprise is that this can be done at all, precisely, with ordinary fractions.'),
          math('P(\\text{event}) = \\frac{\\text{outcomes you want}}{\\text{outcomes altogether}}'),
        ],
      },
      intuition: {
        title: 'A scale from 0 to 1',
        blocks: [
          text('Every probability sits between 0 and 1, and both ends mean something definite.'),
          table(['Value', 'Meaning', 'Example'], [
            ['0', 'impossible', 'rolling a 7 on an ordinary die'],
            ['0.25', 'unlikely', 'drawing a heart from a pack of cards'],
            ['0.5', 'evens', 'a fair coin landing heads'],
            ['0.75', 'likely', 'not drawing a heart'],
            ['1', 'certain', 'rolling a number below 7'],
          ]),
          insight('The denominator is *everything that could happen*, not everything else. In a bag of 3 red and 5 blue counters there are 8 outcomes altogether, so P(red) = 3/8.'),
          warning('Answering 3/5 is a real and different quantity — the **odds**, comparing red to blue. It is a perfectly good way to talk about a horse race, and it is not a probability. A probability always has the total underneath it.', 'Probability or odds?'),
          text('One consequence is worth having straight away, because it turns hard questions into easy ones. Something either happens or it does not, so those two probabilities must add to 1.'),
          math('P(\\text{not } A) = 1 - P(A)'),
          text('Working out the probability of *at least one* head in five tosses is unpleasant head-on. Working out the probability of no heads at all, and subtracting from 1, is not.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: 0, to: 1, step: 0.25, marks: [{ at: 0, label: 'impossible' }, { at: 0.5, label: 'evens' }, { at: 1, label: 'certain' }] },
            'Every probability lives on this line. Nothing sits outside it — a probability of 1.5 or −0.2 is not unlikely, it is a mistake.',
          ),
          text('All of this assumes the outcomes are **equally likely**, which is why the examples are dice, coins and counters drawn without looking. Those are chosen not because they are interesting but because the assumption is honest for them.'),
          tip('When outcomes are not equally likely — will it rain, will the bus be late — probabilities have to be estimated from data instead of counted. That is the next lesson in this strand, and it is where probability meets statistics.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A bag holds 4 red, 6 blue and 2 green counters. One is drawn at random. Find the probability that it is blue, and the probability that it is not blue.'),
          steps([
            { label: 'Count every outcome.', latex: '4 + 6 + 2 = 12' },
            { label: 'Favourable outcomes for blue.', latex: '6' },
            { label: 'Write the probability and simplify.', latex: 'P(\\text{blue}) = \\frac{6}{12} = \\frac{1}{2}' },
            { label: 'Not blue, by subtraction.', latex: '1 - \\frac{1}{2} = \\frac{1}{2}' },
            { label: 'Check by counting directly.', latex: '\\frac{4 + 2}{12} = \\frac{6}{12} = \\frac{1}{2}' },
          ]),
          text('The two routes agree, as they must. On harder questions only one of them is manageable, so it is worth being fluent in both.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('basic-probability', 4)] },
      independent: { title: 'On your own', blocks: [practice('basic-probability', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('basic-probability', 3, 'Complements, and probabilities written as percentages.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Probability runs from 0 (impossible) to 1 (certain).',
            'P(event) = favourable outcomes ÷ total outcomes.',
            'The total goes underneath — comparing to the *rest* gives odds, not probability.',
            'P(not A) = 1 − P(A), which is often the easier way round.',
            'Counting outcomes like this assumes they are equally likely.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'compound-probability-01',
    unitId: 'unit-probability',
    conceptId: 'compound-probability',
    title: 'Compound Events',
    summary: 'Two things happening rather than one — listed as a sample space, or multiplied along the branches of a tree.',
    objectives: [
      'List the sample space of a two-stage experiment and count it',
      'Multiply probabilities to find the chance that both events happen',
      'Adjust the second probability when the first item is not replaced',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two things at once',
        blocks: [
          text('A coin and a die are thrown together. What is the probability of heads *and* a six?'),
          text('Each part is easy on its own — a half, and a sixth. The question is what to do with those two numbers, and there is only one plausible-looking wrong move: adding them.'),
          math('\\tfrac{1}{2} + \\tfrac{1}{6} = \\tfrac{2}{3}'),
          warning('Two thirds would mean this happens more often than not. It happens once in twelve throws. Whenever two conditions must *both* hold, the answer is smaller than either one alone — so adding cannot be right, and a quick size check catches it before any arithmetic.', 'Why adding is not it'),
        ],
      },
      intuition: {
        title: 'A fraction of a fraction',
        blocks: [
          text('Half the throws come up heads. Of *those* throws, a sixth also show a six. So the answer is a sixth of a half — and "of" has meant multiply since Grade 5.'),
          math('\\tfrac{1}{2} \\times \\tfrac{1}{6} = \\tfrac{1}{12}'),
          insight('P(A and B) = P(A) × P(B), when the first event does not change the second. Nothing about a coin affects a die, so the two are **independent**.'),
          text('The other way to see it is to count. Every outcome of the coin pairs with every outcome of the die:'),
          table(['', '1', '2', '3', '4', '5', '6'], [
            ['H', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
            ['T', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6'],
          ]),
          text('Twelve outcomes, all equally likely, and exactly one of them is H6. That table is the **sample space**, and 2 × 6 = 12 is why multiplying works: it counts the grid.'),
          warning('Counting 2 + 6 = 8 outcomes is the mirror of adding the probabilities. The stages are not laid end to end, they are crossed — each row of the table is a whole copy of the second stage.', 'Adding the stages'),
          text('Independence is an assumption, and sometimes a false one. Take two counters from a bag of 3 red and 5 blue **without putting the first back**:'),
          steps([
            { label: 'The first is red.', latex: 'P = \\tfrac{3}{8}' },
            { label: 'One red is now gone, and so is one counter altogether.', latex: 'P = \\tfrac{2}{7}' },
            { label: 'Multiply along the branch.', latex: '\\tfrac{3}{8} \\times \\tfrac{2}{7} = \\tfrac{6}{56} = \\tfrac{3}{28}' },
          ]),
          insight('Both numbers in the second fraction change. Reducing the top and forgetting the bottom is the more common half of the mistake.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('A tree diagram draws the same thing as branches. Each path from left to right is one outcome, and its probability is the product of the numbers along it:'),
          table(['First counter', 'Second counter', 'Path', 'Probability'], [
            ['red (3/8)', 'red (2/7)', 'red, red', '6/56'],
            ['red (3/8)', 'blue (5/7)', 'red, blue', '15/56'],
            ['blue (5/8)', 'red (3/7)', 'blue, red', '15/56'],
            ['blue (5/8)', 'blue (4/7)', 'blue, blue', '20/56'],
          ]),
          insight('The four paths cover everything that can happen, so their probabilities total 1: 6 + 15 + 15 + 20 = 56 out of 56. That total is the best check there is — if it does not come to 1, a branch is wrong or missing.'),
          text('The tree also answers "one of each colour", which no single branch does: red-then-blue and blue-then-red are different paths, and both count. Add those two, giving 30/56.'),
          tip('Multiply *along* the branches, add *between* them. That one sentence covers every compound probability question at this level.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A spinner has 4 equal sections, one of which is gold. It is spun twice. What is the probability of gold both times, and of gold at least once?'),
          steps([
            { label: 'The spinner has no memory, so the spins are independent.', latex: 'P(\\text{gold}) = \\tfrac{1}{4}' },
            { label: 'Both times: multiply.', latex: '\\tfrac{1}{4} \\times \\tfrac{1}{4} = \\tfrac{1}{16}' },
            { label: 'For "at least once", find the opposite first: no gold on either spin.', latex: '\\tfrac{3}{4} \\times \\tfrac{3}{4} = \\tfrac{9}{16}' },
            { label: 'Take that from 1.', latex: '1 - \\tfrac{9}{16} = \\tfrac{7}{16}' },
          ]),
          tip('"At least one" almost always goes faster through the complement. Listing the ways it *can* happen means three cases; the way it cannot means one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('compound-probability', 4)] },
      independent: { title: 'On your own', blocks: [practice('compound-probability', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('compound-probability', 3, 'Draws without replacement, and "at least one" through the complement.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Both events happening: multiply. The answer is smaller than either part.',
            'The sample space of two stages is their product, not their sum — 2 × 6, not 2 + 6.',
            'Multiply along a branch, add between branches.',
            'Without replacement, both the top and the bottom of the second fraction change.',
            'Every branch of a complete tree adds to 1. Use it as a check.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'experimental-probability-01',
    unitId: 'unit-chance',
    conceptId: 'experimental-probability',
    title: 'Experimental and Theoretical Probability',
    summary: 'What a die should do and what it actually did — two different numbers, and the reason they converge.',
    objectives: [
      'Calculate an experimental probability from a record of trials',
      'Compare an experimental result with the theoretical prediction',
      'Say why more trials make an estimate more trustworthy',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Twelve throws, five sixes',
        blocks: [
          text('A fair die should give a six one time in six. A student throws it twelve times and gets five sixes.'),
          text('Nothing has gone wrong, and the die is probably fine. Two different numbers are being talked about, and both are called a probability.'),
          list([
            '**Theoretical probability** — what the counting argument predicts, worked out from the outcomes without touching the die: 1/6.',
            '**Experimental probability** — what actually happened, worked out from the record: 5/12.',
          ]),
        ],
      },
      intuition: {
        title: 'Two numbers, one of them measured',
        blocks: [
          text('The arithmetic of an experimental probability is the same fraction as ever. Only the meaning of the two numbers changes.'),
          math('P_{\\text{experimental}} = \\frac{\\text{times it happened}}{\\text{trials}}'),
          insight('Theoretical probability comes from a *model* of the situation — six faces, all equally likely. Experimental probability comes from *data*, and needs no model at all, which is why it is the only kind available for a drawing pin, a biased spinner, or the chance of rain.'),
          warning('An experimental probability is an estimate, and a short run of trials is a poor one. Five sixes in twelve throws is unremarkable; five hundred in twelve hundred would be strong evidence the die is loaded. The number of trials is part of the answer, not a detail of how it was collected.', 'How many trials?'),
          text('Increase the trials and something dependable happens: the experimental value settles towards the theoretical one. This is the **law of large numbers**, and it is why a casino can lose to any individual player all evening and still know exactly what its year looks like.'),
          table(['Throws', 'Sixes', 'Experimental P', 'Distance from 1/6'], [
            ['12', '5', '0.417', '0.250'],
            ['60', '13', '0.217', '0.050'],
            ['600', '108', '0.180', '0.013'],
            ['6000', '1012', '0.169', '0.002'],
          ]),
          warning('Converging does not mean the die is catching up. A run of sixes is not repaid by a run without them — the die has no memory, and the next throw is 1/6 whatever came before. The early excess is not cancelled; it is simply outvoted by everything that follows.', 'The gambler’s error'),
          insight('Which gives the honest use of each: the theoretical value predicts what a fair thing will do, and the experimental value tests whether the thing is fair. A large gap between them is evidence about the die, not an error in the arithmetic.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '1', value: 9 },
                { label: '2', value: 11 },
                { label: '3', value: 7 },
                { label: '4', value: 12 },
                { label: '5', value: 10 },
                { label: '6', value: 11 },
              ],
              unitLabel: 'throws',
            },
            'Sixty throws of one die. Theory says ten of each; the bars sit near ten and none of them is exactly ten. Perfectly even bars would be the surprising result.',
          ),
          tip('Add the bars before trusting any of them. They must total the number of trials — here 60 — and a total that does not match means an outcome has been missed or double-counted.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A spinner has 4 equal sections, one of them red. In 80 spins it lands on red 24 times. Find both probabilities, and say what the comparison suggests.'),
          steps([
            { label: 'Theoretical: one section in four.', latex: 'P = \\tfrac{1}{4} = 0.25' },
            { label: 'Experimental: reds over spins.', latex: 'P = \\tfrac{24}{80} = 0.3' },
            { label: 'Compare.', latex: '0.3 > 0.25' },
            { label: 'Convert to what was expected.', latex: '80 \\times 0.25 = 20 \\text{ reds expected, } 24 \\text{ seen}' },
          ]),
          text('Four extra reds in eighty spins is a small gap and easily chance; it is not evidence of a weighted spinner. Repeat with eight hundred spins and a gap of that proportion — 240 reds against 200 expected — would be worth taking seriously.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('experimental-probability', 4)] },
      independent: { title: 'On your own', blocks: [practice('experimental-probability', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('experimental-probability', 3, 'Predicting from a rate, and judging how much a short run of trials proves.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Theoretical probability is predicted from the outcomes; experimental probability is measured from trials.',
            'Experimental probability is successes ÷ trials — the trials go underneath.',
            'The two rarely match exactly, and a small gap over few trials means nothing.',
            'More trials bring the experimental value towards the theoretical one.',
            'A coin has no memory: past results never change the next probability.',
            'For anything you cannot model — a drawing pin, the weather — experimental is the only kind there is.',
          ]),
        ],
      },
    },
  }),
];
